<template>
  <div class="prompt-markdown-editor" :class="{ 'prompt-markdown-editor-error': error === true }">
    <div class="prompt-markdown-toolbar" :dir="direction">
      <div class="prompt-mode-switch">
        <button type="button" class="prompt-mode-button" :class="{ 'prompt-mode-button-active': editorMode === 'visual' }" @click="switchEditorMode('visual')">{{ resolveLabel('visualMode') }}</button>
        <button type="button" class="prompt-mode-button" :class="{ 'prompt-mode-button-active': editorMode === 'text' }" @click="switchEditorMode('text')">{{ resolveLabel('textMode') }}</button>
      </div>
      <span class="prompt-toolbar-separator"></span>
      <template v-if="editorMode === 'visual'">
        <button type="button" class="prompt-toolbar-button" @mousedown.prevent="runBlockCommand('p')">{{ resolveLabel('paragraph') }}</button>
        <button type="button" class="prompt-toolbar-button" @mousedown.prevent="runBlockCommand('h2')">{{ resolveLabel('heading') }}</button>
        <button type="button" class="prompt-toolbar-button" @mousedown.prevent="runBlockCommand('h3')">{{ resolveLabel('subheading') }}</button>
        <span class="prompt-toolbar-separator"></span>
        <button type="button" class="prompt-toolbar-button prompt-toolbar-button-strong" @mousedown.prevent="runInlineCommand('bold')">{{ resolveLabel('bold') }}</button>
        <button type="button" class="prompt-toolbar-button prompt-toolbar-button-em" @mousedown.prevent="runInlineCommand('italic')">{{ resolveLabel('italic') }}</button>
        <button type="button" class="prompt-toolbar-button" @mousedown.prevent="openLinkModalFromToolbar">{{ resolveLabel('link') }}</button>
        <span class="prompt-toolbar-separator"></span>
        <button type="button" class="prompt-toolbar-button" @mousedown.prevent="runInlineCommand('insertUnorderedList')">{{ resolveLabel('bullets') }}</button>
        <button type="button" class="prompt-toolbar-button" @mousedown.prevent="runInlineCommand('insertOrderedList')">{{ resolveLabel('numberedList') }}</button>
        <button type="button" class="prompt-toolbar-button" @mousedown.prevent="runBlockCommand('blockquote')">{{ resolveLabel('quote') }}</button>
        <button type="button" class="prompt-toolbar-button" @mousedown.prevent="runBlockCommand('pre')">{{ resolveLabel('codeBlock') }}</button>
        <span class="prompt-toolbar-separator"></span>
        <button type="button" class="prompt-toolbar-button" @mousedown.prevent="insertDivider">{{ resolveLabel('divider') }}</button>
        <button type="button" class="prompt-toolbar-button" @mousedown.prevent="runInlineCommand('removeFormat')">{{ resolveLabel('clear') }}</button>
      </template>
    </div>
    <div class="prompt-markdown-surface-wrap">
      <div
        v-if="editorMode === 'visual'"
        ref="editorEl"
        class="prompt-markdown-surface"
        contenteditable="true"
        role="textbox"
        aria-multiline="true"
        spellcheck="true"
        :dir="direction"
        :data-placeholder="placeholder"
        @input="handleEditorInput"
        @keydown="handleKeydown"
        @paste="handlePaste"
        @click="handleEditorClick"
        @mouseup="handleEditorMouseUp"
        @blur="syncModelFromEditor(true)"
      ></div>
      <textarea
        v-else
        ref="plainTextEl"
        v-model="plainTextValue"
        class="prompt-markdown-textarea textarea textarea-bordered"
        :dir="direction"
        :placeholder="placeholder"
        spellcheck="true"
        @input="handlePlainTextInput"
      ></textarea>
    </div>
    <AppModal :visible="showLinkModal === true" :title="resolveLabel('editLink')" boxClass="max-w-lg w-11/12" @close="closeLinkModal">
      <div class="space-y-4 py-2">
        <label class="form-control w-full">
          <span class="label-text mb-2">{{ resolveLabel('linkText') }}</span>
          <input v-model="linkModalText" type="text" class="input input-bordered w-full" :dir="direction" />
        </label>
        <label class="form-control w-full">
          <span class="label-text mb-2">{{ resolveLabel('linkUrl') }}</span>
          <input v-model="linkModalUrl" type="text" class="input input-bordered w-full" dir="ltr" />
        </label>
      </div>
      <template #actions>
        <button v-if="activeLinkElement !== null" type="button" class="btn btn-error btn-sm" @click="removeActiveLink">{{ resolveLabel('removeLink') }}</button>
        <button type="button" class="btn" @click="closeLinkModal">{{ resolveLabel('cancel') }}</button>
        <button type="button" class="btn" @click="applyLinkFromModal">{{ resolveLabel('saveLink') }}</button>
      </template>
    </AppModal>
  </div>
</template>

<script setup>
import { nextTick, onMounted, ref, watch } from 'vue';
import { marked } from 'marked';
import AppModal from '@/components/AppModal.vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  direction: { type: String, default: 'ltr' },
  error: { type: Boolean, default: false },
  labels: {
    type: Object,
    default: function() {
      return {};
    }
  }
});

const emit = defineEmits(['update:modelValue']);
const editorEl = ref(null);
const plainTextEl = ref(null);
const internalSync = ref(false);
const editorMode = ref('visual');
const plainTextValue = ref('');
const showLinkModal = ref(false);
const linkModalUrl = ref('');
const linkModalText = ref('');
const activeLinkElement = ref(null);
const skipLinkMouseUp = ref(false);
const savedSelectionRange = ref(null);

const fallbackLabels = {
  visualMode: 'Visual',
  textMode: 'Text',
  paragraph: 'Paragraph',
  heading: 'Heading',
  subheading: 'Subheading',
  bold: 'Bold',
  italic: 'Italic',
  link: 'Link',
  bullets: 'Bullets',
  numberedList: 'Numbered list',
  quote: 'Quote',
  codeBlock: 'Code block',
  divider: 'Divider',
  clear: 'Clear',
  editLink: 'Edit Link',
  linkText: 'Link text',
  linkUrl: 'URL',
  saveLink: 'Save',
  cancel: 'Cancel',
  removeLink: 'Remove link'
};

function resolveLabel(key) {
  let provided = '';
  if (props.labels !== undefined && props.labels !== null) {
    provided = props.labels[key];
  }
  if (typeof provided === 'string' && provided.length > 0) {
    return provided;
  }
  const fallback = fallbackLabels[key];
  if (typeof fallback === 'string') {
    return fallback;
  }
  return key;
}

function markdownToHtml(source) {
  if (typeof source !== 'string') {
    return '';
  }
  const parsed = marked.parse(source, { gfm: true, breaks: true });
  if (typeof parsed === 'string') {
    return parsed;
  }
  return '';
}

function convertModelToHtml(value) {
  const source = typeof value === 'string' ? value : '';
  const trimmed = source.trim();
  if (trimmed.length === 0) {
    return '';
  }
  if (/<[a-z][\s\S]*>/i.test(trimmed) === true) {
    return sanitizeHtml(trimmed);
  }
  return sanitizeHtml(markdownToHtml(trimmed));
}

function sanitizeHtml(rawHtml) {
  if (typeof DOMParser === 'undefined') {
    return '';
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString('<div>' + rawHtml + '</div>', 'text/html');
  const container = doc.body.firstChild;
  if (container === undefined) {
    return '';
  }
  if (container === null) {
    return '';
  }
  const allowedTags = new Set(['div', 'span', 'h1', 'h2', 'h3', 'h4', 'p', 'br', 'strong', 'b', 'em', 'i', 'a', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'hr', 'table', 'thead', 'tbody', 'tr', 'th', 'td']);
  function clean(node) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toLowerCase();
      if (allowedTags.has(tag) !== true) {
        const parent = node.parentNode;
        if (parent === null) {
          return;
        }
        while (node.firstChild !== null) {
          parent.insertBefore(node.firstChild, node);
        }
        parent.removeChild(node);
        return;
      }
      if (tag === 'a') {
        const href = node.getAttribute('href') !== undefined && node.getAttribute('href') !== null ? node.getAttribute('href') : '';
        if (isSafeLink(href) !== true) {
          node.removeAttribute('href');
        } else {
          node.setAttribute('target', '_blank');
          node.setAttribute('rel', 'noopener noreferrer');
        }
      }
      const attributes = Array.from(node.attributes);
      for (let index = 0; index < attributes.length; index = index + 1) {
        const attribute = attributes[index];
        if (attribute.name === 'href' && tag === 'a') {
          continue;
        }
        if (attribute.name === 'target' && tag === 'a') {
          continue;
        }
        if (attribute.name === 'rel' && tag === 'a') {
          continue;
        }
        node.removeAttribute(attribute.name);
      }
    }
    let child = node.firstChild;
    while (child !== null) {
      const next = child.nextSibling;
      clean(child);
      child = next;
    }
  }
  clean(container);
  return container.innerHTML.trim();
}

function isSafeLink(url) {
  const value = typeof url === 'string' ? url.trim().toLowerCase() : '';
  if (value.startsWith('http://') === true) {
    return true;
  }
  if (value.startsWith('https://') === true) {
    return true;
  }
  if (value.startsWith('mailto:') === true) {
    return true;
  }
  if (value.startsWith('#') === true) {
    return true;
  }
  return false;
}

function normalizeLinkUrl(url) {
  const value = typeof url === 'string' ? url.trim() : '';
  if (value.length === 0) {
    return '';
  }
  if (isSafeLink(value) === true) {
    return value;
  }
  return 'https://' + value;
}

function setEditorHtml(html) {
  const editor = editorEl.value;
  if (editor === null) {
    return;
  }
  const safeHtml = sanitizeHtml(html);
  if (editor.innerHTML !== safeHtml) {
    editor.innerHTML = safeHtml;
  }
}

function focusEditor() {
  const editor = editorEl.value;
  if (editor === null) {
    return;
  }
  editor.focus();
}

function runInlineCommand(command) {
  focusEditor();
  document.execCommand(command, false, null);
  syncModelFromEditor();
}

function runBlockCommand(tagName) {
  focusEditor();
  document.execCommand('formatBlock', false, tagName);
  syncModelFromEditor();
}

function insertDivider() {
  focusEditor();
  document.execCommand('insertHTML', false, '<hr>');
  syncModelFromEditor();
}

function switchEditorMode(mode) {
  if (mode !== 'visual' && mode !== 'text') {
    return;
  }
  if (editorMode.value === mode) {
    return;
  }
  if (mode === 'text') {
    syncModelFromEditor(false);
    plainTextValue.value = typeof props.modelValue === 'string' ? props.modelValue : '';
    editorMode.value = 'text';
    return;
  }
  internalSync.value = true;
  const nextValue = typeof plainTextValue.value === 'string' ? plainTextValue.value : '';
  emit('update:modelValue', nextValue);
  editorMode.value = 'visual';
  nextTick(() => {
    setEditorHtml(convertModelToHtml(nextValue));
    internalSync.value = false;
  });
}

function handlePlainTextInput() {
  internalSync.value = true;
  emit('update:modelValue', plainTextValue.value);
  nextTick(() => {
    internalSync.value = false;
  });
}

function looksLikeUrl(text) {
  const value = typeof text === 'string' ? text.trim() : '';
  if (value.length === 0) {
    return false;
  }
  if (value.startsWith('http://') === true) {
    return true;
  }
  if (value.startsWith('https://') === true) {
    return true;
  }
  if (value.startsWith('mailto:') === true) {
    return true;
  }
  if (/^[\w.-]+\.[a-z]{2,}(\/|$)/i.test(value) === true) {
    return true;
  }
  return false;
}

function getSelectedLinkElement() {
  if (typeof window.getSelection !== 'function') {
    return null;
  }
  const selection = window.getSelection();
  if (selection === null || selection.rangeCount === 0) {
    return null;
  }
  let node = selection.anchorNode;
  if (node === null) {
    return null;
  }
  if (node.nodeType === Node.TEXT_NODE) {
    node = node.parentElement;
  }
  if (node === null || node === undefined) {
    return null;
  }
  if (node.tagName !== undefined && node.tagName.toLowerCase() === 'a') {
    return node;
  }
  if (node.closest !== undefined) {
    return node.closest('a');
  }
  return null;
}

function saveCurrentSelection() {
  savedSelectionRange.value = null;
  if (typeof window.getSelection !== 'function') {
    return;
  }
  const selection = window.getSelection();
  if (selection === null || selection.rangeCount === 0) {
    return;
  }
  savedSelectionRange.value = selection.getRangeAt(0).cloneRange();
}

function restoreCurrentSelection() {
  if (savedSelectionRange.value === null) {
    return;
  }
  if (typeof window.getSelection !== 'function') {
    return;
  }
  const selection = window.getSelection();
  if (selection === null) {
    return;
  }
  selection.removeAllRanges();
  selection.addRange(savedSelectionRange.value);
}

function openLinkModalForElement(linkNode) {
  if (linkNode === null || linkNode === undefined) {
    return;
  }
  const href = linkNode.getAttribute('href');
  linkModalUrl.value = href !== null && href !== undefined ? href : '';
  linkModalText.value = typeof linkNode.textContent === 'string' ? linkNode.textContent.trim() : '';
  activeLinkElement.value = linkNode;
  showLinkModal.value = true;
}

function openLinkModalFromToolbar() {
  focusEditor();
  saveCurrentSelection();
  const existingLink = getSelectedLinkElement();
  if (existingLink !== null) {
    openLinkModalForElement(existingLink);
    return;
  }
  activeLinkElement.value = null;
  let selectedText = '';
  if (typeof window.getSelection === 'function') {
    const selection = window.getSelection();
    if (selection !== null) {
      selectedText = selection.toString().trim();
    }
  }
  if (looksLikeUrl(selectedText) === true) {
    linkModalUrl.value = normalizeLinkUrl(selectedText);
    linkModalText.value = selectedText;
  } else {
    linkModalUrl.value = 'https://';
    linkModalText.value = selectedText;
  }
  showLinkModal.value = true;
}

function closeLinkModal() {
  showLinkModal.value = false;
  activeLinkElement.value = null;
  linkModalUrl.value = '';
  linkModalText.value = '';
  savedSelectionRange.value = null;
}

function applyLinkFromModal() {
  const url = normalizeLinkUrl(linkModalUrl.value);
  if (url.length === 0) {
    return;
  }
  const text = typeof linkModalText.value === 'string' ? linkModalText.value.trim() : '';
  const existingLink = activeLinkElement.value;
  if (existingLink !== null && existingLink !== undefined) {
    existingLink.setAttribute('href', url);
    existingLink.setAttribute('target', '_blank');
    existingLink.setAttribute('rel', 'noopener noreferrer');
    existingLink.textContent = text.length > 0 ? text : url;
    closeLinkModal();
    syncModelFromEditor(true);
    return;
  }
  focusEditor();
  restoreCurrentSelection();
  if (text.length > 0) {
    document.execCommand('insertHTML', false, '<a href="' + escapeAttribute(url) + '" target="_blank" rel="noopener noreferrer">' + escapeHtml(text) + '</a>');
  } else if (typeof window.getSelection === 'function') {
    const selection = window.getSelection();
    if (selection !== null && selection.toString().trim().length > 0) {
      document.execCommand('createLink', false, url);
    } else {
      document.execCommand('insertHTML', false, '<a href="' + escapeAttribute(url) + '" target="_blank" rel="noopener noreferrer">' + escapeHtml(url) + '</a>');
    }
  }
  closeLinkModal();
  syncModelFromEditor(true);
}

function removeActiveLink() {
  const link = activeLinkElement.value;
  if (link === null || link === undefined) {
    return;
  }
  const parent = link.parentNode;
  if (parent === null) {
    closeLinkModal();
    return;
  }
  while (link.firstChild !== null) {
    parent.insertBefore(link.firstChild, link);
  }
  parent.removeChild(link);
  closeLinkModal();
  syncModelFromEditor(true);
}

function handleEditorClick(event) {
  if (editorMode.value !== 'visual') {
    return;
  }
  const target = event.target;
  if (target === null || target === undefined) {
    return;
  }
  let linkNode = null;
  if (target.nodeType === Node.ELEMENT_NODE && target.tagName.toLowerCase() === 'a') {
    linkNode = target;
  } else if (target.closest !== undefined) {
    linkNode = target.closest('a');
  }
  if (linkNode !== null) {
    event.preventDefault();
    skipLinkMouseUp.value = true;
    openLinkModalForElement(linkNode);
  }
}

function handleEditorMouseUp() {
  if (editorMode.value !== 'visual') {
    return;
  }
  if (skipLinkMouseUp.value === true) {
    skipLinkMouseUp.value = false;
    return;
  }
  const existingLink = getSelectedLinkElement();
  if (existingLink !== null) {
    return;
  }
  if (typeof window.getSelection !== 'function') {
    return;
  }
  const selection = window.getSelection();
  if (selection === null || selection.isCollapsed === true) {
    return;
  }
  const selectedText = selection.toString().trim();
  if (selectedText.length === 0) {
    return;
  }
  if (looksLikeUrl(selectedText) !== true) {
    return;
  }
  activeLinkElement.value = null;
  linkModalUrl.value = normalizeLinkUrl(selectedText);
  linkModalText.value = selectedText;
  saveCurrentSelection();
  showLinkModal.value = true;
}

function handleKeydown(event) {
  if (event.key === 'Tab') {
    event.preventDefault();
    document.execCommand('insertText', false, '  ');
    syncModelFromEditor();
  }
}

function handlePaste(event) {
  event.preventDefault();
  let html = '';
  let text = '';
  if (event.clipboardData !== undefined && event.clipboardData !== null) {
    html = event.clipboardData.getData('text/html');
    text = event.clipboardData.getData('text/plain');
  }
  if (html.length > 0) {
    document.execCommand('insertHTML', false, sanitizeHtml(html));
    syncModelFromEditor(true);
    return;
  }
  if (text.length > 0) {
    document.execCommand('insertHTML', false, sanitizeHtml(markdownToHtml(text)));
    syncModelFromEditor(true);
  }
}

function handleEditorInput() {
  syncModelFromEditor();
}

function syncModelFromEditor(rerenderFromMarkdown) {
  const editor = editorEl.value;
  if (editor === null) {
    return;
  }
  const markdown = htmlToMarkdown(editor.innerHTML);
  if (markdown.length === 0) {
    const editorText = typeof editor.textContent === 'string' ? editor.textContent : '';
    if (editorText.trim().length === 0) {
      editor.innerHTML = '';
    }
  }
  internalSync.value = true;
  emit('update:modelValue', markdown);
  if (rerenderFromMarkdown === true) {
    setEditorHtml(convertModelToHtml(markdown));
  }
  nextTick(() => {
    internalSync.value = false;
  });
}

function htmlToMarkdown(html) {
  if (typeof DOMParser === 'undefined') {
    return '';
  }
  const safeHtml = sanitizeHtml(html);
  const parser = new DOMParser();
  const doc = parser.parseFromString('<div>' + safeHtml + '</div>', 'text/html');
  const container = doc.body.firstChild;
  if (container === undefined) {
    return '';
  }
  if (container === null) {
    return '';
  }
  return trimBlock(renderChildren(container)).replace(/\n{3,}/g, '\n\n');
}

function renderChildren(node) {
  let result = '';
  let child = node.firstChild;
  while (child !== null) {
    result = result + renderNode(child);
    child = child.nextSibling;
  }
  return result;
}

function renderNode(node) {
  if (node.nodeType === Node.TEXT_NODE) {
    return normalizeText(node.textContent);
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return '';
  }
  const tag = node.tagName.toLowerCase();
  if (tag === 'br') {
    return '\n';
  }
  if (tag === 'hr') {
    return '\n---\n\n';
  }
  if (tag === 'strong') {
    return '**' + trimInline(renderChildren(node)) + '**';
  }
  if (tag === 'b') {
    return '**' + trimInline(renderChildren(node)) + '**';
  }
  if (tag === 'em') {
    return '*' + trimInline(renderChildren(node)) + '*';
  }
  if (tag === 'i') {
    return '*' + trimInline(renderChildren(node)) + '*';
  }
  if (tag === 'code') {
    if (node.parentElement !== null && node.parentElement.tagName.toLowerCase() === 'pre') {
      return normalizeText(node.textContent);
    }
    return '`' + trimInline(node.textContent) + '`';
  }
  if (tag === 'pre') {
    return renderCodeBlock(node);
  }
  if (tag === 'a') {
    return renderLink(node);
  }
  if (tag === 'ul') {
    return renderList(node, false);
  }
  if (tag === 'ol') {
    return renderList(node, true);
  }
  if (tag === 'blockquote') {
    return renderQuote(node);
  }
  if (tag === 'table') {
    return renderTable(node);
  }
  if (tag === 'thead') {
    return renderChildren(node);
  }
  if (tag === 'tbody') {
    return renderChildren(node);
  }
  if (tag === 'tr') {
    return renderChildren(node);
  }
  if (tag === 'th') {
    return renderChildren(node);
  }
  if (tag === 'td') {
    return renderChildren(node);
  }
  if (isHeadingTag(tag) === true) {
    return renderHeading(node, tag);
  }
  if (tag === 'p') {
    return renderBlock(node);
  }
  if (tag === 'div') {
    return renderBlock(node);
  }
  if (tag === 'span') {
    return renderChildren(node);
  }
  return renderChildren(node);
}

function isHeadingTag(tag) {
  if (tag === 'h1') {
    return true;
  }
  if (tag === 'h2') {
    return true;
  }
  if (tag === 'h3') {
    return true;
  }
  if (tag === 'h4') {
    return true;
  }
  return false;
}

function renderHeading(node, tag) {
  let prefix = '# ';
  if (tag === 'h2') {
    prefix = '## ';
  }
  if (tag === 'h3') {
    prefix = '### ';
  }
  if (tag === 'h4') {
    prefix = '#### ';
  }
  const text = trimBlock(renderChildren(node));
  if (text.length === 0) {
    return '';
  }
  return prefix + text + '\n\n';
}

function renderBlock(node) {
  const text = trimBlock(renderChildren(node));
  if (text.length === 0) {
    return '\n';
  }
  return text + '\n\n';
}

function renderList(node, ordered) {
  let result = '';
  let index = 1;
  let child = node.firstElementChild;
  while (child !== null) {
    if (child.tagName.toLowerCase() === 'li') {
      const body = trimBlock(renderChildren(child));
      const prefix = ordered === true ? String(index) + '. ' : '- ';
      result = result + renderListItem(body, prefix);
      index = index + 1;
    }
    child = child.nextElementSibling;
  }
  return result + '\n';
}

function renderListItem(body, prefix) {
  if (body.length === 0) {
    return prefix + '\n';
  }
  const lines = body.split('\n');
  let result = '';
  for (let index = 0; index < lines.length; index = index + 1) {
    const line = lines[index];
    if (index === 0) {
      result = result + prefix + line + '\n';
    } else {
      result = result + '   ' + line + '\n';
    }
  }
  return result;
}

function renderQuote(node) {
  const text = trimBlock(renderChildren(node));
  if (text.length === 0) {
    return '';
  }
  const lines = text.split('\n');
  let result = '';
  for (let index = 0; index < lines.length; index = index + 1) {
    const line = lines[index];
    if (line.trim().length > 0) {
      result = result + '> ' + line + '\n';
    } else {
      result = result + '>\n';
    }
  }
  return result + '\n';
}

function extractTableRowCells(rowNode) {
  const cells = [];
  let child = rowNode.firstChild;
  while (child !== null) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const tag = child.tagName.toLowerCase();
      if (tag === 'th' || tag === 'td') {
        cells.push(trimInline(renderChildren(child)));
      }
    }
    child = child.nextSibling;
  }
  return cells;
}

function collectTableRows(tableNode) {
  const headerRows = [];
  const bodyRows = [];
  let child = tableNode.firstChild;
  while (child !== null) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const tag = child.tagName.toLowerCase();
      if (tag === 'thead') {
        let rowNode = child.firstChild;
        while (rowNode !== null) {
          if (rowNode.nodeType === Node.ELEMENT_NODE && rowNode.tagName.toLowerCase() === 'tr') {
            headerRows.push(extractTableRowCells(rowNode));
          }
          rowNode = rowNode.nextSibling;
        }
      }
      if (tag === 'tbody') {
        let rowNode = child.firstChild;
        while (rowNode !== null) {
          if (rowNode.nodeType === Node.ELEMENT_NODE && rowNode.tagName.toLowerCase() === 'tr') {
            bodyRows.push(extractTableRowCells(rowNode));
          }
          rowNode = rowNode.nextSibling;
        }
      }
      if (tag === 'tr') {
        bodyRows.push(extractTableRowCells(child));
      }
    }
    child = child.nextSibling;
  }
  return { headerRows: headerRows, bodyRows: bodyRows };
}

function renderTable(node) {
  const tableData = collectTableRows(node);
  let headerRow = null;
  if (tableData.headerRows.length > 0) {
    headerRow = tableData.headerRows[0];
  }
  const bodyRows = tableData.bodyRows.slice();
  if (headerRow === null && bodyRows.length > 0) {
    headerRow = bodyRows.shift();
  }
  if (headerRow === null || headerRow === undefined || headerRow.length === 0) {
    return '';
  }
  const columnCount = headerRow.length;
  let result = '| ' + headerRow.join(' | ') + ' |\n';
  result = result + '|';
  for (let index = 0; index < columnCount; index = index + 1) {
    result = result + ' --- |';
  }
  result = result + '\n';
  for (let rowIndex = 0; rowIndex < bodyRows.length; rowIndex = rowIndex + 1) {
    const sourceRow = bodyRows[rowIndex];
    const normalizedRow = [];
    for (let cellIndex = 0; cellIndex < columnCount; cellIndex = cellIndex + 1) {
      if (cellIndex < sourceRow.length) {
        normalizedRow.push(sourceRow[cellIndex]);
      } else {
        normalizedRow.push('');
      }
    }
    result = result + '| ' + normalizedRow.join(' | ') + ' |\n';
  }
  return result + '\n';
}

function renderCodeBlock(node) {
  const text = normalizeText(node.textContent).replace(/\n+$/g, '');
  if (text.length === 0) {
    return '';
  }
  return '```\n' + text + '\n```\n\n';
}

function renderLink(node) {
  const href = node.getAttribute('href') !== null ? node.getAttribute('href') : '';
  const text = trimInline(renderChildren(node));
  if (href.length === 0) {
    return text;
  }
  if (text.length === 0) {
    return href;
  }
  return '[' + text + '](' + href + ')';
}

function normalizeText(text) {
  const value = typeof text === 'string' ? text : '';
  return value.replace(/\u00a0/g, ' ');
}

function trimBlock(value) {
  const source = typeof value === 'string' ? value : '';
  return source.replace(/[ \t]+\n/g, '\n').replace(/\n[ \t]+/g, '\n').trim();
}

function trimInline(value) {
  const source = typeof value === 'string' ? value : '';
  return source.replace(/\s+/g, ' ').trim();
}

function escapeHtml(value) {
  const source = typeof value === 'string' ? value : '';
  return source.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function escapeAttribute(value) {
  return escapeHtml(value).replace(/'/g, '&#39;');
}

onMounted(() => {
  plainTextValue.value = typeof props.modelValue === 'string' ? props.modelValue : '';
  if (editorMode.value === 'visual') {
    setEditorHtml(convertModelToHtml(props.modelValue));
  }
});

watch(
  () => props.modelValue,
  (value) => {
    if (internalSync.value === true) {
      return;
    }
    if (editorMode.value === 'text') {
      plainTextValue.value = typeof value === 'string' ? value : '';
      return;
    }
    setEditorHtml(convertModelToHtml(value));
  }
);
</script>

<style scoped>
.prompt-markdown-editor {
  border: 1px solid rgba(148, 163, 184, 0.45);
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.96));
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08);
  overflow: hidden;
}
.prompt-markdown-editor-error {
  border-color: rgb(239, 68, 68);
  box-shadow: 0 18px 45px rgba(239, 68, 68, 0.12);
}
.prompt-markdown-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 0.75rem 0.85rem 0.35rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.25);
  background: rgba(248, 250, 252, 0.9);
}
.prompt-mode-switch {
  display: inline-flex;
  align-items: center;
  border: 1px solid rgba(148, 163, 184, 0.55);
  border-radius: 999px;
  overflow: hidden;
  margin: 0 0.45rem 0.45rem 0;
  background: rgba(255, 255, 255, 0.92);
}
.prompt-mode-button {
  height: 2rem;
  border: 0;
  padding: 0 0.9rem;
  background: transparent;
  color: rgb(71, 85, 105);
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1;
}
.prompt-mode-button-active {
  background: rgb(15, 118, 110);
  color: rgb(255, 255, 255);
}
.prompt-markdown-toolbar[dir='rtl'] .prompt-mode-switch {
  margin: 0 0 0.45rem 0.45rem;
}
.prompt-toolbar-button {
  height: 2rem;
  border: 1px solid rgba(148, 163, 184, 0.55);
  border-radius: 999px;
  padding: 0 0.78rem;
  margin: 0 0.45rem 0.45rem 0;
  background: rgba(255, 255, 255, 0.92);
  color: rgb(30, 41, 59);
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1;
  transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease, color 160ms ease;
}
.prompt-toolbar-button:hover {
  border-color: rgb(16, 163, 127);
  color: rgb(15, 118, 110);
  box-shadow: 0 8px 22px rgba(15, 118, 110, 0.12);
  transform: translateY(-1px);
}
.prompt-toolbar-button:active {
  transform: translateY(0);
}
.prompt-toolbar-button-strong {
  font-weight: 800;
}
.prompt-toolbar-button-em {
  font-style: italic;
}
.prompt-toolbar-separator {
  width: 1px;
  height: 1.45rem;
  margin: 0 0.45rem 0.45rem 0;
  background: rgba(148, 163, 184, 0.45);
}
.prompt-markdown-toolbar[dir='rtl'] .prompt-toolbar-button {
  margin: 0 0 0.45rem 0.45rem;
}
.prompt-markdown-toolbar[dir='rtl'] .prompt-toolbar-separator {
  margin: 0 0 0.45rem 0.45rem;
}
.prompt-markdown-surface-wrap {
  background: rgba(255, 255, 255, 0.94);
}
.prompt-markdown-textarea {
  min-height: 24rem;
  max-height: 42rem;
  width: 100%;
  resize: vertical;
  border: 0;
  border-radius: 0;
  padding: 1.15rem 1.25rem 1.35rem;
  color: rgb(15, 23, 42);
  font-size: 0.92rem;
  line-height: 1.65;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  background: transparent;
  box-shadow: none;
}
.prompt-markdown-surface {
  min-height: 24rem;
  max-height: 42rem;
  overflow: auto;
  padding: 1.15rem 1.25rem 1.35rem;
  color: rgb(15, 23, 42);
  font-size: 0.95rem;
  line-height: 1.72;
  outline: none;
}
.prompt-markdown-surface:empty:before {
  content: attr(data-placeholder);
  color: rgba(100, 116, 139, 0.82);
}
.prompt-markdown-surface :deep(h1),
.prompt-markdown-surface :deep(h2),
.prompt-markdown-surface :deep(h3),
.prompt-markdown-surface :deep(h4) {
  color: rgb(15, 23, 42);
  font-weight: 800;
  line-height: 1.25;
  margin: 0.9rem 0 0.45rem;
}
.prompt-markdown-surface :deep(h1) {
  font-size: 1.55rem;
}
.prompt-markdown-surface :deep(h2) {
  font-size: 1.35rem;
}
.prompt-markdown-surface :deep(h3) {
  font-size: 1.12rem;
}
.prompt-markdown-surface :deep(p) {
  margin: 0 0 0.85rem;
}
.prompt-markdown-surface :deep(ul),
.prompt-markdown-surface :deep(ol) {
  margin: 0.55rem 0 0.95rem 1.35rem;
  padding: 0;
}
.prompt-markdown-surface[dir='rtl'] :deep(ul),
.prompt-markdown-surface[dir='rtl'] :deep(ol) {
  margin: 0.55rem 1.35rem 0.95rem 0;
}
.prompt-markdown-surface :deep(li) {
  margin: 0.18rem 0;
}
.prompt-markdown-surface :deep(blockquote) {
  margin: 0.85rem 0;
  padding: 0.65rem 0.9rem;
  border-inline-start: 4px solid rgb(16, 163, 127);
  border-radius: 12px;
  background: rgba(16, 163, 127, 0.08);
  color: rgb(51, 65, 85);
}
.prompt-markdown-surface :deep(pre) {
  margin: 0.9rem 0;
  padding: 0.9rem 1rem;
  border-radius: 14px;
  background: rgb(15, 23, 42);
  color: rgb(226, 232, 240);
  overflow: auto;
}
.prompt-markdown-surface :deep(code) {
  border-radius: 6px;
  padding: 0.12rem 0.32rem;
  background: rgba(15, 23, 42, 0.08);
  color: rgb(15, 23, 42);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.9em;
}
.prompt-markdown-surface :deep(pre code) {
  padding: 0;
  background: transparent;
  color: inherit;
}
.prompt-markdown-surface :deep(a) {
  color: rgb(15, 118, 110);
  text-decoration: underline;
  font-weight: 600;
}
.prompt-markdown-surface :deep(hr) {
  border: 0;
  border-top: 1px solid rgba(148, 163, 184, 0.45);
  margin: 1rem 0;
}
.prompt-markdown-surface :deep(table) {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin: 0.9rem 0;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 12px;
  overflow: hidden;
  background-color: #ffffff;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
}
.prompt-markdown-surface :deep(th) {
  background-color: rgba(15, 23, 42, 0.06);
  color: #0f172a;
  font-weight: 600;
  font-size: 0.86rem;
  text-align: start;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.35);
}
.prompt-markdown-surface :deep(td) {
  color: #1f2937;
  font-size: 0.86rem;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
  vertical-align: top;
}
.prompt-markdown-surface :deep(tr:last-child td) {
  border-bottom: none;
}
:global(.dark) .prompt-markdown-editor {
  border-color: rgba(71, 85, 105, 0.75);
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.96), rgba(2, 6, 23, 0.96));
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.3);
}
:global(.dark) .prompt-markdown-toolbar {
  border-bottom-color: rgba(71, 85, 105, 0.65);
  background: rgba(15, 23, 42, 0.9);
}
:global(.dark) .prompt-toolbar-button {
  border-color: rgba(100, 116, 139, 0.7);
  background: rgba(30, 41, 59, 0.88);
  color: rgb(226, 232, 240);
}
:global(.dark) .prompt-markdown-surface-wrap {
  background: rgba(15, 23, 42, 0.92);
}
:global(.dark) .prompt-markdown-textarea {
  color: rgb(226, 232, 240);
  background: transparent;
}
:global(.dark) .prompt-mode-switch {
  background: rgba(30, 41, 59, 0.88);
  border-color: rgba(100, 116, 139, 0.7);
}
:global(.dark) .prompt-mode-button {
  color: rgb(203, 213, 225);
}
:global(.dark) .prompt-mode-button-active {
  background: rgb(20, 184, 166);
  color: rgb(15, 23, 42);
}
:global(.dark) .prompt-markdown-surface {
  color: rgb(226, 232, 240);
}
:global(.dark) .prompt-markdown-surface :deep(h1),
:global(.dark) .prompt-markdown-surface :deep(h2),
:global(.dark) .prompt-markdown-surface :deep(h3),
:global(.dark) .prompt-markdown-surface :deep(h4) {
  color: rgb(248, 250, 252);
}
:global(.dark) .prompt-markdown-surface :deep(blockquote) {
  background: rgba(20, 184, 166, 0.12);
  color: rgb(203, 213, 225);
}
:global(.dark) .prompt-markdown-surface :deep(code) {
  background: rgba(226, 232, 240, 0.12);
  color: rgb(226, 232, 240);
}
:global(.dark) .prompt-markdown-surface :deep(table) {
  background-color: #0b1220;
  border-color: rgba(71, 85, 105, 0.75);
}
:global(.dark) .prompt-markdown-surface :deep(th) {
  background-color: rgba(148, 163, 184, 0.1);
  color: rgb(229, 231, 235);
  border-bottom-color: rgba(148, 163, 184, 0.15);
}
:global(.dark) .prompt-markdown-surface :deep(td) {
  color: rgb(229, 231, 235);
  border-bottom-color: rgba(148, 163, 184, 0.15);
}
</style>
